class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    /*
      Delegated on this element, not bound to the overlay node.

      #CartDrawer-Overlay lives INSIDE #CartDrawer, and every re-render — a
      quantity change, refreshFromServer, the free-gift watcher — replaces
      #CartDrawer's innerHTML wholesale. A listener bound to the node went with
      it, and from then on clicking the scrim did nothing: the drawer could
      only be closed by the X or Escape.

      Binding here instead survives every one of those, and cannot throw on a
      page where the overlay has not rendered.
    */
    this.addEventListener('click', (event) => {
      if (event.target.closest?.('#CartDrawer-Overlay')) this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    /*
      Delegated on the document rather than bound to the anchor.

      This used to attach a listener to #cart-icon-bubble once, from the
      constructor. Three things break that:

        - anything that replaces the anchor takes the listener with it (a theme
          editor section reload, a sticky header re-render), and from then on
          the Bag is a plain link to /cart — the intermittent behaviour;
        - a cart link rendered after this element upgraded is never bound at
          all, and the drawer is rendered ABOVE the header in theme.liquid;
        - only one link can carry an id, so a second cart link anywhere else
          would navigate.

      A document listener survives all of it and needs no re-binding, ever.
    */
    if (CartDrawer.delegated) return;
    CartDrawer.delegated = true;

    const cartPath = ((window.routes && window.routes.cart_url) || '/cart').replace(/\/$/, '');

    const cartLinkFrom = (target) => {
      const el = target instanceof Element ? target.closest('a, button, [role="button"]') : null;
      if (!el) return null;

      // Links inside the drawer (View cart, checkout) must still navigate,
      // or there would be no way to reach the cart page at all.
      if (el.closest('cart-drawer')) return null;

      if (el.id === 'cart-icon-bubble' || el.hasAttribute('data-cart-drawer-open')) return el;

      const href = el.getAttribute('href');
      if (!href) return null;
      return href.split('?')[0].replace(/\/$/, '') === cartPath ? el : null;
    };

    const openFrom = (event, link) => {
      // No drawer on this page — leave the link alone rather than dead-end it.
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      event.preventDefault();
      drawer.open(link);
    };

    document.addEventListener('click', (event) => {
      const link = cartLinkFrom(event.target);
      if (link) openFrom(event, link);
    });

    document.addEventListener('keydown', (event) => {
      if (!event.code || event.code.toUpperCase() !== 'SPACE') return;
      const link = cartLinkFrom(event.target);
      if (link) openFrom(event, link);
    });

    // The roles go back on whenever the header returns.
    const markLinks = () => {
      document.querySelectorAll('#cart-icon-bubble, [data-cart-drawer-open]').forEach((link) => {
        if (link.closest('cart-drawer')) return;
        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');
      });
    };
    markLinks();
    document.addEventListener('shopify:section:load', markLinks);
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        /*
          Chosen from the markup, not from the is-empty class. The two can
          disagree — adding from a product card leaves the class on while the
          fresh markup is the filled drawer — and the class-led version then
          looked for .drawer__inner-empty, got null, and threw inside
          trapFocus. Falling back to this element means it can never be null.
        */
        const containerToTrapFocusOn =
          this.querySelector('.drawer__inner-empty') || document.getElementById('CartDrawer') || this;
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    /*
      Dawn removes is-empty from .drawer__inner, but the class lives on this
      element — so that line has never done anything, and the class survived
      every add made from outside product-form.js. Set from what just rendered
      instead: the empty branch is the only thing that produces
      .drawer__inner-empty, so its presence is the state.

      It matters beyond focus: while the class is wrongly on, Dawn hides
      .drawer__header and centres .drawer__inner in a one-row grid, which is
      what made a drawer with an item in it look half empty.
    */
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const markup = parsedState.sections?.[section.id];
      if (!markup) {
        console.warn(`[cart-drawer] the server returned no "${section.id}" section; leaving it as it was`);
        return;
      }

      /*
        A section with no selector is swapped whole into the element it was
        asked for. Only a section that names a selector gets the fallback list,
        and each candidate is written into its OWN element — resolving the
        target from the candidate without that split wrote the bag count into
        the first .shopify-section on the page instead of the bag.
      */
      let applied = false;

      if (section.selector) {
        for (const candidate of [section.selector, '#CartDrawer', '.drawer__inner']) {
          const inner = this.getSectionInnerHTML(markup, candidate);
          const target = document.querySelector(candidate);
          if (inner === null || !target) continue;
          target.innerHTML = inner;
          applied = true;
          break;
        }
      } else {
        const inner = this.getSectionInnerHTML(markup);
        if (inner !== null) {
          sectionElement.innerHTML = inner;
          applied = true;
        }
      }

      if (!applied) {
        console.warn(`[cart-drawer] could not apply the "${section.id}" section`);
      }
    });

    this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  /*
    Re-reads the cart from the server and repaints.

    The safety net for "the item went in but the screen did not move". Painting
    from the add response can fail, and by then the cart has already changed —
    a stale bag count is worse than an error, because a shopper who sees
    Bag (0) simply adds the thing again.

    Every caller can reach this, so the recovery is not duplicated per entry
    point and cannot drift between them.
  */
  refreshFromServer() {
    return fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => response.json())
      .then((sections) => {
        const parse = (html, selector) =>
          new DOMParser().parseFromString(html || '', 'text/html').querySelector(selector);

        const bubble = document.getElementById('cart-icon-bubble');
        const freshBubble = parse(sections['cart-icon-bubble'], '.shopify-section');
        if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;

        const inner = document.getElementById('CartDrawer');
        const freshDrawer = parse(sections['cart-drawer'], '#CartDrawer');
        if (inner && freshDrawer) inner.innerHTML = freshDrawer.innerHTML;

        this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));
        this.open();
      })
      .catch((error) => console.error('[cart-drawer] could not re-read the cart', error));
  }

  getSectionInnerHTML(html, selector = '.shopify-section', quiet = true) {
    /*
      Returns null instead of throwing when the element is not in the response.

      Shopify does not always wrap a section the way the default selector
      expects, and one missing wrapper used to throw out of the forEach that
      calls this — abandoning every other section with it. The item was in the
      cart, the drawer and the count were left stale, and nothing recovered
      until a reload.

      A section with no wrapper is still the section, so the default selector
      falls back to the parsed body. A specific selector that is genuinely
      absent returns null and the caller skips that one alone.
    */
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const found = parsed.querySelector(selector);
    if (found) return found.innerHTML;

    if (selector === '.shopify-section') return parsed.body.innerHTML;

    if (!quiet) console.warn(`[cart] "${selector}" was not in the returned section`);
    return null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
